import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const SESSION_KEY_PREFIX = 'parent_lock_unlocked_';

export function isUnlockedThisSession(userId: string | undefined): boolean {
  if (!userId) return false;
  try {
    return sessionStorage.getItem(SESSION_KEY_PREFIX + userId) === '1';
  } catch {
    return false;
  }
}

export function markUnlockedThisSession(userId: string | undefined) {
  if (!userId) return;
  try {
    sessionStorage.setItem(SESSION_KEY_PREFIX + userId, '1');
  } catch {
    // sessionStorage can throw in private mode; non-fatal
  }
}

interface PinGateProps {
  userId: string | undefined;
  onUnlocked: () => void;
}

export const PinGate: React.FC<PinGateProps> = ({ userId, onUnlocked }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^\d{4}$/.test(pin)) {
      setError('Enter the 4-digit PIN.');
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.rpc('verify_parent_lock_pin', { p_pin: pin });
      if (error) {
        setError(error.message);
        return;
      }
      if (data === true) {
        markUnlockedThisSession(userId);
        onUnlocked();
      } else {
        setError('Incorrect PIN.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="max-w-sm mx-auto p-6 mt-12">
      <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
        <Lock className="h-4 w-4" />
        <span>This view is parent-locked.</span>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label htmlFor="parent-pin">Enter PIN</Label>
          <Input
            id="parent-pin"
            type="password"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            autoComplete="off"
            placeholder="••••"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button type="submit" disabled={submitting || pin.length !== 4} className="w-full">
          {submitting ? 'Checking…' : 'Unlock'}
        </Button>
      </form>
    </Card>
  );
};
